// /src/hooks/useUrlFilters.js
// react
import { useCallback, useEffect, useState } from 'react';

// components
import useSearchParams from '@/hooks/useStableSearchParams';

const normalize = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (typeof v === 'string' && v.trim() === '') return;
    if (Array.isArray(v) && v.length === 0) return;
    if (typeof v === 'object' && Object.keys(v).length === 0) return;
    out[k] = v;
  });
  return out;
};

export default function useUrlFilters(defaultFilters = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const read = useCallback(() => {
    const raw = searchParams.get('filters');
    if (!raw) return defaultFilters;
    try {
      const decoded = decodeURIComponent(raw);
      const parsed = JSON.parse(decoded);
      return { ...defaultFilters, ...parsed };
    } catch {
      return defaultFilters;
    }
  }, [searchParams, defaultFilters]);

  const [filters, setFiltersState] = useState(read);

  // sync when user navigates back/forward
  useEffect(() => {
    const next = read();
    if (JSON.stringify(next) !== JSON.stringify(filters)) {
      setFiltersState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const write = useCallback(
    (nextObj, { push = false } = {}) => {
      const next = normalize(nextObj);
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (Object.keys(next).length === 0) {
            sp.delete('filters');
          } else {
            sp.set('filters', encodeURIComponent(JSON.stringify(next)));
          }
          return sp;
        },
        { replace: !push },
      );
      setFiltersState(next);
    },
    [setSearchParams],
  );

  const applyFilters = useCallback(
    (partial) => {
      write({ ...(filters || {}), ...(partial || {}) });
    },
    [filters, write],
  );

  const clearFilters = useCallback(() => write({}), [write]);

  return {
    filters: filters || {},
    setFilters: write, // set entire object
    applyFilters, // shallow-merge partials
    clearFilters,
  };
}
